import { useContext, useEffect, useMemo, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { addTranslationForLanguage, setActiveLanguage } from 'react-localize-redux'
import { bindActionCreators } from 'redux'
import { isFunction } from 'lodash'
import { useLocation, useHistory, useRouteMatch } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGlobe, faInfoCircle } from '@fortawesome/pro-duotone-svg-icons'

import InterneoAccountForm from './InterneoAccountForm'
import appConfig from '../../config'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { getIdOrNull } from '../../utils'
import {
  getInstitutionGroupInternshipRequestPortal,
  getInstitutionInternshipRequestPortal
} from '../../utils/api/internshipRequestPortal'
import { LanguageLocales } from '../../utils/constants'
import { setLanguage } from '../../utils/locale'
import { ColorsPalette } from '../../config/colors'
import AppLoading from '../../Components/shared/AppLoading'
import { Properties } from '../../utils/entities'
import InternshipRequestPortalFormProvider, { Modes } from '../../Providers/Forms/InternshipRequestPortal/InternshipRequestPortalFormProvider'
import { RequestPortalContext } from '../../Providers/RequestPortalProvider'
import { Routes } from '../../routes'
import AnonymousLanguagesMenu from './AnonymousLanguagesMenu'

const PortalAccess = Object.freeze({
  INSTITUTION: 'INSTITUTION',
  INSTITUTION_GROUP: 'INSTITUTION_GROUP'
})

const UriParams = Object.freeze({
  ACCESS_KEY: 'portal-access-key',
  INSTITUTION: 'institution',
  INSTITUTION_GROUP: 'institution-group'
})

const FORM_STYLE = {
  width: '550px',
  maxWidth: '90vw',
  borderRadius: '15px'
}

const InternshipRequestPortal = () => {
  const { setInstitution } = useContext(RequestPortalContext)

  const t = useSelector(selectTranslate)
  const location = useLocation()
  const dispatch = useDispatch()
  const history = useHistory()

  const dispatchSetActiveLanguage = useMemo(() => bindActionCreators(setActiveLanguage, dispatch), [dispatch])
  const dispatchAddTranslationForLanguage = useMemo(() => bindActionCreators(addTranslationForLanguage, dispatch), [dispatch])

  const [loadedLanguageTag, setLoadedLanguageTag] = useState('')
  const [portalData, setPortalData] = useState({ institution: null, institutionGroup: null, accessKey: null })
  const [errorMessage, setErrorMessage] = useState('')
  const [portalMode, setPortalMode] = useState('')

  const studentMatch = useRouteMatch(Routes.INTERNSHIP_REQUEST_PORTAL_STUDENT)
  const isStudentPortal = studentMatch?.isExact === true

  useEffect(() => {
    if (!(getIdOrNull(portalData.institution) || getIdOrNull(portalData.institutionGroup))) {
      const searchParams = new URLSearchParams(location.search)
      const accessKey = searchParams.get(UriParams.ACCESS_KEY)

      const config = { request: null, property: null }

      if (location.search.includes(UriParams[PortalAccess.INSTITUTION_GROUP])) {
        config.property = PortalAccess.INSTITUTION_GROUP
        config.request = getInstitutionGroupInternshipRequestPortal
      } else if (location.search.includes(UriParams[PortalAccess.INSTITUTION])) {
        config.property = PortalAccess.INSTITUTION
        config.request = getInstitutionInternshipRequestPortal
      }

      if (isFunction(config.request) && config.property !== null) {
        const { request, property } = config

        request(searchParams.get(UriParams[property]), accessKey, isStudentPortal)
          .then(({ data }) => setPortalData(portalData => ({ ...portalData, [Properties[property]]: data, accessKey })))
          .catch(reason => reason.json().then(({ message }) => setErrorMessage(message)))
      } else {
        setErrorMessage('internship_request_portal.malformed_link')
      }
    }

    const language = portalData.institution?.localeLanguage?.languageTag ?? portalData.institutionGroup?.localeLanguage?.languageTag ?? LanguageLocales.FR_BE

    setLanguage({
      language,
      addTranslationForLanguage: dispatchAddTranslationForLanguage,
      setActiveLanguage: dispatchSetActiveLanguage
    }).then(() => setLoadedLanguageTag(language))
  }, [portalData.institution, portalData.institutionGroup, location.search, setActiveLanguage, dispatchAddTranslationForLanguage, dispatchSetActiveLanguage, isStudentPortal])

  const handleHasAccount = () => {
    setInstitution(portalData.institution)
    history.push(Routes.LOGIN)
  }

  if (loadedLanguageTag.length === 0 && (portalData.institution === null || portalData.institutionGroup === null)) {
    return <AppLoading />
  }

  return (
    <div className='flex-column anonymous-form-wrapper'>
      <div className='flex-fill' />

      <div className='form-bg' />

      <div className='form-grid'>
        <div style={FORM_STYLE} className='flex-column form-wrapper'>
          <div style={{ position: 'relative', alignSelf: 'flex-end' }}>
            <AnonymousLanguagesMenu icon={faGlobe} />
          </div>

          <div className='login-form'>
            <div className='logo-wrapper pdb-4'>
              <div className='title-wrapper'>
                <img src='/assets/interneo_logo.svg' alt='interneo-logo' />
                <div className='version'>V{appConfig.version}</div>
              </div>
            </div>

            <div className='labels'>
              <div className='title'>{t('internship_request_portal.title')}</div>
              {getIdOrNull(portalData.institutionGroup) !== null && (
                <div className='sub-title'>{portalData.institutionGroup.name}</div>
              )}
              {getIdOrNull(portalData.institutionGroup) == null && getIdOrNull(portalData.institution) !== null && (
                <div className='sub-title'>{portalData.institution.name}</div>
              )}
            </div>
          </div>

          <div className='flex-fill' />

          {(errorMessage.length !== 0) && (
            <>
              <div className='flex-fill' />

              <div className='modal-inner-container warning'>
                <div className='flex-row ai-bl'>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    color={ColorsPalette.WARNING_FONT}
                  />
                  <span className='pdl-4'><strong>{t('internship_request_portal.broken_link')}</strong></span>
                </div>
                <br />

                <p>
                  <span>{t('internship_request_portal.redirect.title')}</span>
                  <span className='pdl-4'>{t('internship_request_portal.redirect.call_to_action')} <a href='https://app.interneo.pro/login'>{t('internship_request_portal.redirect.button')}</a>.</span>
                </p>
              </div>

              <div className='flex-fill' />
            </>
          )}

          {(getIdOrNull(portalData.institutionGroup) || getIdOrNull(portalData.institution)) && (
            <InternshipRequestPortalFormProvider
              institutionGroup={portalData.institutionGroup}
              institution={portalData.institution}
              portalAccessKey={portalData.accessKey}
              initialMode={isStudentPortal ? Modes.STUDENT : null}
              visible={portalMode.length > 0}
              onReset={() => setPortalMode('')}
              Wrapper={(({ children }) => <div>{children}</div>)}
            >
              <InterneoAccountForm
                forcedMode={isStudentPortal ? Modes.STUDENT : null}
                showSchoolChoice={false}
                visible={portalMode === ''}
                onPortalRedirection={setPortalMode}
                onHasAccount={handleHasAccount}
              />
            </InternshipRequestPortalFormProvider>
          )}
        </div>
      </div>

      <div className='flex-fill' />
    </div>
  )
}

export default InternshipRequestPortal
